/** Settings → Notifications (Figma "Settings — Notifications (alert preferences)"): what each person gets by e-mail. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AppWindow,
  Check,
  ChartColumn,
  ChevronDown,
  CreditCard,
  Globe,
  Lock,
  Mail,
  RefreshCw,
  Server,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, FullPageSpinner, Toggle, cx, useToast } from "@/components/ui";
import { DraftOffer } from "@/components/ui/FormDraft";
import { portal } from "@/lib/api";
import { useFormDraft } from "@/lib/form-drafts";
import { timeAgo } from "@/lib/format";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import type { DigestFrequency, EmailPreferences, EmailUpdate } from "@/lib/types";

import { useEmailPreferences } from "./General";
import { WeavexMark } from "./parts";

const CRITICAL: { icon: LucideIcon; title: string; detail: string }[] = [
  {
    icon: ShieldCheck,
    title: "Security & sign-in",
    detail: "New device sign-ins, password and permission changes",
  },
  {
    icon: CreditCard,
    title: "Payments & billing",
    detail: "Failed charges, grace periods and cancellations",
  },
  { icon: Globe, title: "Domains & registry", detail: "Expiry, failed renewals and registry changes" },
  { icon: RefreshCw, title: "Service outages", detail: "When a service is degraded, and when it recovers" },
];

const UPDATES: { key: EmailUpdate; icon: ReactNode; title: string; detail: string; unavailable?: string }[] =
  [
    {
      key: "digest",
      icon: <ChartColumn className="size-4" strokeWidth={1.75} />,
      title: "Performance digest",
      detail: "Visitors, enquiries and what changed",
    },
    {
      key: "domains_hosting",
      icon: <Server className="size-4" strokeWidth={1.75} />,
      title: "Domains & hosting",
      detail: "Renewal reminders, DNS changes, deployments",
    },
    {
      key: "email",
      icon: <Mail className="size-4" strokeWidth={1.75} />,
      title: "Email",
      detail: "Mailbox setup and delivery problems",
      unavailable: "No mailboxes on this business yet",
    },
    {
      key: "team",
      icon: <Users className="size-4" strokeWidth={1.75} />,
      title: "Team & access",
      detail: "Invites accepted, role changes, access requests",
    },
    {
      key: "website",
      icon: <AppWindow className="size-4" strokeWidth={1.75} />,
      title: "Website",
      detail: "Publish results and free-limit warnings",
    },
    {
      key: "weavex_suggestions",
      icon: <WeavexMark className="size-4" />,
      title: "Weavex suggestions",
      detail: "When Weavex has a new recommendation for your website",
    },
    {
      key: "growth_intelligence",
      icon: <ShieldCheck className="size-4" strokeWidth={1.75} />,
      title: "Growth Intelligence",
      detail: "New sourced insights and recommendations",
      unavailable: "Not available for your business yet",
    },
  ];

const FREQUENCIES: [DigestFrequency, string][] = [
  ["daily", "Daily · every morning"],
  ["weekly", "Weekly · Mondays"],
  ["monthly", "Monthly · 1st of the month"],
];

export function NotificationsTab() {
  const prefs = useEmailPreferences();
  if (prefs.isPending) return <FullPageSpinner />;
  if (prefs.isError) return <ErrorAlert error={prefs.error} />;
  return <PreferencesForm saved={prefs.data} />;
}

function PreferencesForm({ saved }: { saved: EmailPreferences }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState(saved);
  const choices = useMemo(
    () => ({ updates: draft.updates, digest_frequency: draft.digest_frequency }),
    [draft.updates, draft.digest_frequency],
  );
  const kept = useFormDraft("settings.notifications", choices);
  const save = useMutation({
    mutationFn: () => portal.saveEmailPreferences(orgId, draft),
    onSuccess: (s) => {
      kept.clear();
      qc.setQueryData(["email-preferences", orgId], s);
      setDraft(s);
      toast.show({ tone: "success", title: "Notification settings saved" });
    },
  });
  const changes =
    UPDATES.filter(({ key }) => draft.updates[key] !== saved.updates[key]).length +
    Number(draft.digest_frequency !== saved.digest_frequency);
  const unavailable = new Set(saved.unavailable);
  const unsaved = useUnsavedChanges(changes > 0 && !save.isPending);

  return (
    <div className="flex flex-col items-start gap-4 lg:flex-row">
      {unsaved.modal}
      <div className="flex w-full min-w-0 flex-1 flex-col gap-5">
        <DraftOffer draft={kept} onContinue={(c) => setDraft((d) => ({ ...d, ...c }))} />
        <AlertsCard
          title="Critical alerts"
          detail="Security, money and anything that could take your site or email offline. Always sent by email."
          badge={
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#f3ebe7] py-1 pr-2.5 pl-2 text-[11.5px] font-semibold text-[#5c5551]">
              <Lock aria-hidden className="size-[11px]" />
              Can’t be turned off
            </span>
          }
        >
          {CRITICAL.map(({ icon: I, title, detail }) => (
            <AlertRow
              key={title}
              icon={<I className="size-4" strokeWidth={1.75} />}
              title={title}
              detail={detail}
            >
              <span className="inline-flex items-center gap-1 rounded-full bg-[#d9f0e6] px-2 py-[3px] text-[11px] font-semibold text-[#13734d]">
                <Lock aria-hidden className="size-[11px]" />
                On
              </span>
            </AlertRow>
          ))}
        </AlertsCard>

        <AlertsCard title="Business updates" detail="Optional. Choose which updates you’d like by email.">
          {UPDATES.map(({ key, icon, title, detail, unavailable: why }) => {
            const off = unavailable.has(key);
            return (
              <AlertRow
                key={key}
                icon={icon}
                title={title}
                detail={detail}
                dimmed={off}
                extra={
                  off ? (
                    <span className="inline-flex rounded-full bg-[#f3ebe7] py-[3px] pr-[9px] pl-2 text-[11px] font-semibold text-fg-subtle">
                      {why}
                    </span>
                  ) : key === "digest" ? (
                    <span className="relative inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[11.5px] font-semibold text-[#5c5551]">
                      {FREQUENCIES.find(([f]) => f === draft.digest_frequency)?.[1]}
                      <ChevronDown aria-hidden className="size-3.5" />
                      <select
                        aria-label="Digest frequency"
                        value={draft.digest_frequency}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, digest_frequency: e.target.value as DigestFrequency }))
                        }
                        className="absolute inset-0 cursor-pointer opacity-0"
                      >
                        {FREQUENCIES.map(([f, label]) => (
                          <option key={f} value={f}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </span>
                  ) : undefined
                }
              >
                <Toggle
                  label={title}
                  checked={!off && draft.updates[key]}
                  disabledReason={off ? why : undefined}
                  onChange={(on) => setDraft((d) => ({ ...d, updates: { ...d.updates, [key]: on } }))}
                />
              </AlertRow>
            );
          })}
        </AlertsCard>

        <ErrorAlert error={save.error} />
        {changes > 0 && (
          <div className="sticky bottom-4 flex w-full items-center gap-2.5 rounded-xl bg-[#221c19] py-3 pr-3 pl-[18px] shadow-lg">
            <p className="min-w-0 flex-1 text-[13px] font-semibold text-white">
              {changes} unsaved change{changes === 1 ? "" : "s"}
              {kept.savedAt && (
                <span className="ml-2 font-normal text-white/70">· draft saved {timeAgo(kept.savedAt)}</span>
              )}
            </p>
            <button
              type="button"
              className="rounded-full px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-white/10"
              onClick={() => {
                setDraft(saved);
                kept.startFresh();
              }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={save.isPending}
              aria-busy={save.isPending}
              onClick={() => save.mutate()}
              className="rounded-full bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {save.isPending ? "Saving…" : "Save preferences"}
            </button>
          </div>
        )}
      </div>

      <aside className="flex w-full shrink-0 flex-col gap-3 rounded-[14px] border border-border bg-brand-50 px-[21px] py-[19px] lg:w-[322px]">
        <h2 className="text-[13.5px] font-bold text-fg">How this works</h2>
        {[
          "Critical alerts always reach you by email.",
          "You only get updates for services this business uses.",
          "Each teammate sets their own preferences.",
        ].map((t) => (
          <p key={t} className="flex gap-2 text-[12.5px] leading-[1.5] text-[#5c5551]">
            <Check aria-hidden className="mt-[3px] size-3 shrink-0 text-[#13734d]" strokeWidth={2.5} />
            {t}
          </p>
        ))}
        <p className="border-t border-border pt-3 text-[12.5px] leading-[1.5] text-[#5c5551]">
          To hide a category from your in-app list, use the preferences in{" "}
          <Link to="/notifications" className="font-semibold text-fg underline">
            Notifications
          </Link>
          .
        </p>
      </aside>
    </div>
  );
}

function AlertsCard({
  title,
  detail,
  badge,
  children,
}: {
  title: string;
  detail: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex w-full flex-col rounded-[18px] border border-border bg-surface px-[22px] pt-5 pb-2">
      <div className="flex items-start gap-2.5 pb-1">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <h2 className="text-[17px] font-bold text-fg">{title}</h2>
          <p className="text-[12.5px] leading-[1.5] text-fg-subtle">{detail}</p>
        </div>
        {badge && <span className="pt-2.5">{badge}</span>}
      </div>
      <div className="flex items-center border-b border-border pt-3 pb-2.5 text-[11px] font-semibold tracking-[0.55px] text-fg-subtle uppercase">
        <span className="flex-1">Alert</span>
        <span className="w-[78px] text-center">Email</span>
      </div>
      <div className="flex flex-col divide-y divide-border">{children}</div>
    </section>
  );
}

function AlertRow({
  icon,
  title,
  detail,
  extra,
  dimmed = false,
  children,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  extra?: ReactNode;
  dimmed?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cx("flex items-center py-3.5", dimmed && "opacity-75")}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          aria-hidden
          className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] border border-border bg-brand-50 text-[#5c5551]"
        >
          {icon}
        </span>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-[3px]">
          <p className="text-sm font-semibold text-fg">{title}</p>
          <p className="text-xs leading-[1.45] text-fg-subtle">{detail}</p>
          {extra}
        </div>
      </div>
      <div className="flex w-[78px] shrink-0 justify-center">{children}</div>
    </div>
  );
}
